// shared/src/prompts/p-contrarian-skeptic.ts
// Chain V2 — per-ticker contrarian skeptic. DEEP path only (portfolio).
// Cross-fed: receives the consensus analyst draft as structural steel-man.
// Per design-notes L21 + L7.

import { z } from 'zod';
import { SignalConfidenceBucketEnum } from '../schemas/signal.js';
import { coercedEnum, coerceCiteTag } from '../resolvers/llm-enum-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.3.0';   // 2026-08-07 — disconfirming_evidence per-ROW omission hardening (tolerantRowArray: drop malformed rows). 2026-07-10 — disconfirming_evidence + cross_fed_counter .default([]) (omission-safe; empty is the valid D39 value). 2026-05-25 — add 'news' to cite enum
export const STEP    = 'p.contrarian.skeptic';
export const MODEL   = 'deepseek-v4-flash';
// 2026-07-31 — raised 6000 → 12000. THE ALARM FOUND THIS ONE, not a human: on
// the day the llm-cap-headroom rule shipped it immediately reported
// p.contrarian.skeptic at 75% of cap (4495 tok, of which 4217 reasoning), the
// most exposed step in the entire fleet — higher than any M-stage step that the
// truncation investigation had been looking at. The investigation had queried
// only `m.%` steps and therefore never saw it.
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 12000)
export const ROLE    = 'skeptic' as const;
export const DESC    = 'Red-teams the per-ticker contrarian call against the consensus draft' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  // D39 (2026-05-28): dropped .min(1) — empty array OK when target is unimpeachable.
  // 2026-08-07: per-ROW omission hardening — a present evidence row that dropped
  // `point` or whose `cite` is unrecoverable is DROPPED + logged (not a brief kill).
  disconfirming_evidence: tolerantRowArray(z.object({
    cite:  coercedEnum([
      'ticker_data', 'momentum', 'analyst_output', 'opposing_draft',
      'macro', 'news', 'factual_error', 'reasonability',
    ], coerceCiteTag),
    point: z.string().min(1),
  }), 'p.contrarian.skeptic.disconfirming_evidence'),   // 2026-07-10: omitted WHOLE key still defaults to [] (D39 valid-empty)
  cross_fed_counter: z.array(llmStringItem).default([]),   // 2026-07-10: same omission-fragility class — empty = no opposing-draft counters
  conviction:        SignalConfidenceBucketEnum,
  reasoning:         z.string(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `You are the per-ticker SKEPTIC challenging the per-ticker contrarian analyst's draft. Cross-fed: the user message contains the consensus analyst's parallel draft as your structural steel-man counter.

## ROLE
- READ contrarian analyst's per-ticker draft (direction, change_pct, CI, mechanism, ticker_alignment).
- USE consensus draft (the opposing per-ticker analyst's parallel output) as steel-man source material.
- USE ticker_data (price + meta), momentum signals (code-derived RSI/MACD/state), macro payload, news payload.
- USE FACTUAL CHECKS (forward-date discipline, current-state numeric grounding).
- NO calibration cites. THIS BRIEF, THIS TICKER, THIS DRAFT.

## INPUTS YOU RECEIVE
- ticker, ticker_data, momentum, macro (current payload)
- own_draft       (contrarian analyst's per-ticker draft)
- opposing_draft  (consensus analyst's per-ticker draft — your steel-man)
- ticker news (last 48h)

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "disconfirming_evidence": [
    { "cite":  "ticker_data" | "momentum" | "analyst_output" | "opposing_draft" | "macro" | "news" | "factual_error" | "reasonability",
      "point": "<≤25w specific challenge>" }
  ],
  "cross_fed_counter": [
    "<short ≤20w summary of each consensus-draft point cited as opposing_draft>"
  ],
  "conviction": "HIGH" | "MED" | "LOW",
  "reasoning":  "<headline ≤15w. Then 1-2 sentences (≤50w total) threading the disconfirming evidence.>"
}

## CITE TYPES
- ticker_data: current price / volume / beta contradicts contrarian's change_pct or direction
- momentum:    RSI / MACD / pattern signal disagrees with contrarian directional call
- analyst_output: internal inconsistency (CI bounds violate direction, mechanism contradicts change_pct, etc.)
- opposing_draft: the consensus per-ticker analyst made a SPECIFIC point about this ticker that contrarian failed to address
- macro:       a macro field=value contradicts contrarian's ticker-specific mechanism
- news:          ticker news (last 48h) contradicts contrarian directional call or catalyst
- factual_error: forward-field stale date, current-state numeric mismatch with payload
- reasonability: change_pct magnitude exceeds 2× ticker hist-vol; CI bounds inconsistent with reported direction

## RULES
1. disconfirming_evidence: emit honest challenges. Empty array OK when target is genuinely unimpeachable (D39 — no forced ≥1).
2. Every opposing_draft cite MUST appear in cross_fed_counter.
3. NO calibration cites.
4. conviction default MED. HIGH when ≥2 factual_error OR ≥2 opposing_draft cites contrarian failed to address. LOW when consensus mostly agrees with contrarian framing.
5. Delta-only — no restating contrarian's view.
6. NO new fact invention.
7. factual_error cites: emit conviction at least MED.
`;

export interface PContrarianSkepticInput {
  readonly ticker:          string;
  readonly ticker_data:     Record<string, unknown>;
  readonly momentum:        Record<string, unknown>;
  readonly macro:           Record<string, unknown>;
  readonly own_draft:       Record<string, unknown>;     // p.contrarian.analyst output
  readonly opposing_draft:  Record<string, unknown>;     // p.consensus.analyst output (CROSS-FED)
  readonly news?:           ReadonlyArray<LlmNewsItem>;
  readonly grounding?:      WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function draftExcerpt(d: Record<string, unknown> | undefined, label: string): string {
  if (!d || Object.keys(d).length === 0) return `  (no ${label} draft)`;
  return [
    `  direction:       ${fmt(d['direction'])}`,
    `  change_pct:      ${fmt(d['change_pct'])}`,
    `  change_ci_low:   ${fmt(d['change_ci_low'])}`,
    `  change_ci_high:  ${fmt(d['change_ci_high'])}`,
    `  horizon_days:    ${fmt(d['horizon_days'])}`,
    `  ticker_alignment:${fmt(d['ticker_alignment'])}`,
    `  is_headline:     ${fmt(d['is_headline'])}`,
    `  mechanism:       ${fmt(d['mechanism'])}`,
  ].join('\n');
}

function tickerExcerpt(td: Record<string, unknown>): string {
  return Object.entries(td).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)';
}

function momentumExcerpt(m: Record<string, unknown>): string {
  return Object.entries(m).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)';
}

export function buildUserPrompt(input: PContrarianSkepticInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  return `${groundingBlock}TICKER: ${input.ticker}

TICKER DATA:
${tickerExcerpt(input.ticker_data)}

MOMENTUM SIGNALS:
${momentumExcerpt(input.momentum)}

MACRO PAYLOAD (last 48h):
${Object.entries(input.macro).slice(0, 20).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

OWN_DRAFT (contrarian per-ticker analyst — challenge this):
${draftExcerpt(input.own_draft, 'contrarian')}

OPPOSING_DRAFT (consensus per-ticker analyst — your structural steel-man counter):
${draftExcerpt(input.opposing_draft, 'consensus')}

TICKER NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_p_contrarian_skeptic

Emit a SINGLE JSON object matching the schema. disconfirming_evidence may be empty (D39 — no forced ≥1). Every opposing_draft cite MUST appear in cross_fed_counter.`;
}
